'use client';

import * as React from 'react';
import { useCallback } from 'react';
import { XIcon } from 'lucide-react';

import { Command as CommandPrimitive } from 'cmdk';
import { Command } from './Command';
import { Badge } from './Badge';
import { PiPlus } from 'react-icons/pi';
import { Button } from './Button';

type ListInputProps = {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
};

export const ListInput = React.forwardRef<HTMLDivElement, ListInputProps>(
  ({ value, onChange, placeholder }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    const [inputValue, setInputValue] = React.useState('');

    const handleUnselect = React.useCallback(
      (deselected: string) => {
        onChange(
          value ? value.filter((valueItem) => valueItem !== deselected) : [],
        );
      },
      [value, onChange],
    );

    const onAdd = useCallback(() => {
      if (inputValue.trim()) {
        onChange(value ? [...value, inputValue.trim()] : [inputValue.trim()]);
        setInputValue('');
      }
    }, [value, inputValue, onChange]);

    const handleBlur = useCallback(() => {
      onAdd();
    }, [onAdd]);

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (input) {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            if (input.value === '') {
              onChange(value ? value.slice(0, -1) : []); // Removes the last item from the array
            }
          }
          // This is not a default behaviour of the <input /> field
          if (e.key === 'Escape') {
            input.blur();
          }

          if (e.key === 'Enter') {
            onAdd();
          }
        }
      },
      [onChange, value, onAdd],
    );

    return (
      <Command
        onKeyDown={handleKeyDown}
        className="overflow-visible bg-transparent"
      >
        <div className="group border border-input px-3 py-1 text-sm rounded-md">
          <div className="flex gap-1 flex-wrap">
            {value.map((valueItem) => {
              return (
                <Badge key={valueItem} variant="secondary">
                  {valueItem}
                  <button
                    className="ml-1 ring-offset-background rounded-full outline-none"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleUnselect(valueItem);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={() => handleUnselect(valueItem)}
                  >
                    <XIcon className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </button>
                </Badge>
              );
            })}
            <CommandPrimitive.Input
              ref={inputRef}
              value={inputValue}
              onValueChange={setInputValue}
              onBlur={handleBlur}
              placeholder={placeholder}
              className="ml-2 bg-transparent outline-none placeholder:text-muted-foreground flex-1"
            />
            <Button
              variant="ghost"
              size="icon"
              className="size-8 flex group/btn p-0"
              onClick={() => onAdd()}
            >
              <PiPlus className="group-hover/btn:text-primary"></PiPlus>
            </Button>
          </div>
        </div>
      </Command>
    );
  },
);
